import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';
import { DocumentViewerProvider } from './contexts/DocumentViewerContext';
import { AskSidebarProvider } from './contexts/AskSidebarContext';
import { RedactionModeProvider } from './contexts/RedactionModeContext';
import { OpenCasesProvider } from './contexts/OpenCasesContext';
import { DocumentProgressProvider } from './contexts/DocumentProgressContext';
import { SessionTimeoutGate } from './components/SessionTimeoutGate';


// Import the actual pages
import { LandingPage } from './pages/LandingPage';
import { SignInOnlyPage } from './pages/SignInOnlyPage';
import { SignUpPage } from './pages/SignUpPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { SubscriptionSuccessPage } from './pages/SubscriptionSuccessPage';
import { SubscriptionCancelPage } from './pages/SubscriptionCancelPage';
import { DashboardPage } from './pages/DashboardPage';
import { CaseListPage } from './pages/CaseListPage';

import { CaseDetailPage } from './pages/CaseDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { AccountPage } from './pages/AccountPage';
import { AboutPage } from './pages/AboutPage';
import { HelpPage } from './pages/HelpPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { LogoPreviewPage } from './pages/LogoPreviewPage';
import { TemplateListPage } from './pages/TemplateListPage';
import { TemplateDetailPage } from './pages/TemplateDetailPage';
import { TemplateFormPage } from './pages/TemplateFormPage';
import { ReportListPage } from './pages/ReportListPage';
import { ReportViewerPage } from './pages/ReportViewerPage';
import { ReportEditorPage } from './pages/ReportEditorPage';
import { DebugAuthPage } from './pages/DebugAuthPage';
import { ImpersonatePage } from './pages/ImpersonatePage';
import { MfaVerifyPage } from './pages/MfaVerifyPage';
import { MfaSetupPage } from './pages/MfaSetupPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { DemoPage } from './pages/DemoPage';
import { BookDemoSchedulePage } from './pages/BookDemoSchedulePage';
import { ContactPage } from './pages/ContactPage';
import { SecurityPage } from './pages/SecurityPage';
import { TeamPage } from './pages/TeamPage';
import { JobsPage } from './pages/JobsPage';
import { BlogListPage } from './pages/BlogListPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { EmailVerificationPendingPage } from './pages/EmailVerificationPendingPage';
import { EmailVerifyPage } from './pages/EmailVerifyPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { SocialDemoPage } from './pages/SocialDemoPage';
import { ConferencePage } from './pages/ConferencePage';

function App() {
  return (
    <AuthProvider>
      <div className="min-h-screen bg-medical-50">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/social-demo" element={<SocialDemoPage />} />
          <Route path="/conference" element={<ConferencePage />} />
          <Route path="/logo-preview" element={<LogoPreviewPage />} />
          <Route path="/signin" element={<SignInOnlyPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/mfa-verify" element={<MfaVerifyPage />} />
          <Route path="/impersonate" element={<ImpersonatePage />} />
          <Route path="/signup/mfa" element={<MfaSetupPage onboarding />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/demo/schedule" element={<BookDemoSchedulePage />} />
          {/* Backwards-compat redirects: any inbound /book-demo links land on /demo */}
          <Route path="/book-demo" element={<Navigate to="/demo" replace />} />
          <Route path="/book-demo/schedule" element={<Navigate to="/demo/schedule" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancelPage />} />
          <Route path="/verify-email/pending" element={<EmailVerificationPendingPage />} />
          <Route path="/verify-email" element={<EmailVerifyPage />} />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <SessionTimeoutGate>
              <OpenCasesProvider>
              <RedactionModeProvider>
              <DocumentViewerProvider>
              <DocumentProgressProvider>
              <AskSidebarProvider>
              <Layout>
                <Routes>
                  <Route index element={<Navigate to="/home" replace />} />
                  <Route path="/home" element={<DashboardPage />} />
                  <Route path="/cases" element={<CaseListPage />} />

                  <Route path="/cases/:caseId/*" element={<CaseDetailPage />} />
                  <Route path="/templates" element={<TemplateListPage />} />
                  <Route path="/templates/new" element={<TemplateFormPage />} />
                  <Route path="/templates/:id/edit" element={<TemplateFormPage />} />
                  <Route path="/templates/:id" element={<TemplateDetailPage />} />
                  <Route path="/drafts" element={<ReportListPage />} />
                  <Route path="/drafts/generate" element={<Navigate to="/drafts" replace />} />
                  <Route path="/drafts/:reportId" element={<ReportViewerPage />} />
                  <Route path="/drafts/:reportId/edit" element={<ReportEditorPage />} />
                  <Route path="/debug-auth" element={<DebugAuthPage />} />
                  <Route path="/account" element={<AccountPage />} />
                  <Route path="/billing" element={<Navigate to="/account" replace />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/mfa" element={<MfaSetupPage />} />
                  <Route path="/settings/feedback" element={<FeedbackPage />} />
                  <Route path="/audit" element={<Navigate to="/account?tab=activity" replace />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="*" element={
                    <div className="p-8 text-center">
                      <h1 className="text-2xl font-bold text-medical-900 mb-4">Page Not Found</h1>
                      <p className="text-medical-600">The page you're looking for doesn't exist.</p>
                    </div>
                  } />
                </Routes>
              </Layout>
              </AskSidebarProvider>
              </DocumentProgressProvider>
              </DocumentViewerProvider>
              </RedactionModeProvider>
              </OpenCasesProvider>
              </SessionTimeoutGate>
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </AuthProvider>
  );
}

export default App;